/**
 * Dashboard Setup Card — State 1 waiting room
 *
 * Glassmorphic card with pulsing orbital ring animation, status text,
 * and support contact. Shown when user's workspace is being provisioned.
 */

import { Mail } from "lucide-react";
import { motion } from "framer-motion";

interface DashboardSetupCardProps {
  title: string;
  description: string;
  contactEmail: string;
}

export function DashboardSetupCard({ title, description, contactEmail }: DashboardSetupCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      role="status"
      aria-live="polite"
      className="bg-card rounded-xl border border-border p-8"
    >
      <div className="flex flex-col items-center text-center gap-5">
        {/* Pulsing orbital ring */}
        <div
          aria-hidden="true"
          className="w-12 h-12 rounded-full bg-gradient-to-b from-[rgb(173,126,225)] to-[rgb(42,100,187)] p-[3px] animate-pulse-ring motion-reduce:animate-none motion-reduce:opacity-100"
        >
          <div className="w-full h-full rounded-full bg-card" />
        </div>

        {/* Status text */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-foreground">
            {title}
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {description}
          </p>
        </div>

        {/* Contact row */}
        <a
          href={`mailto:${contactEmail}`}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Mail className="w-4 h-4" />
          <span>{contactEmail}</span>
        </a>
      </div>
    </motion.div>
  );
}
